import React, { useEffect, useRef, useState } from "react";
import { useDarkMode } from "../../../context/DarkModeContext";
import {
  ArrowLeft,
  Clock,
  User,
  Calendar,
  Share2,
  Bookmark,
  Eye,
} from "lucide-react";
import { useParams, useNavigate } from "react-router-dom";

interface Article {
  id: string;
  title: string;
  description: string;
  category: string;
  readTime: string;
  author: string;
  date: string;
  content: string;
}

const ArticleDetail: React.FC = () => {
  const { isDark } = useDarkMode();
  const { articleId } = useParams<{ articleId: string }>();
  const navigate = useNavigate();
  const contentRef = useRef<HTMLDivElement>(null);
  const [readingProgress, setReadingProgress] = useState(0);
  const [isBookmarked, setIsBookmarked] = useState(false);

  const articles: Record<string, Article> = {
    "premium-vs-free": {
      id: "premium-vs-free",
      title: "Premium vs Free: Feature Comparison",
      description:
        "Understand the key differences between our free and premium plans to make the best choice for your job search.",
      category: "plans",
      readTime: "5 min read",
      author: "Find-Work Team",
      date: "2024-01-15",
      content: `
          <h2>Choosing the Right Plan for Your Job Search</h2>
          <p>When it comes to finding your dream job, having the right tools can make all the difference. Our platform offers both free and premium plans designed to meet different needs and budgets.</p>
          
          <h3>Free Plan Features</h3>
          <ul>
            <li><strong>Basic job search</strong> - Access to our standard job database</li>
            <li><strong>5 job applications</strong> per month</li>
            <li><strong>Standard resume upload</strong> - Basic formatting support</li>
            <li><strong>Email notifications</strong> for new matches</li>
            <li><strong>Community support</strong> through forums</li>
            <li><strong>Basic profile visibility</strong> to employers</li>
          </ul>
          
          <h3>Premium Plan Benefits</h3>
          <ul>
            <li><strong>Unlimited job applications</strong> - Apply to as many jobs as you want</li>
            <li><strong>Advanced job matching algorithm</strong> - Get more accurate recommendations</li>
            <li><strong>Priority applicant status</strong> - Your applications move to the top</li>
            <li><strong>Detailed analytics dashboard</strong> - Track application performance</li>
            <li><strong>Direct contact with recruiters</strong> - Message employers directly</li>
            <li><strong>Resume review and optimization</strong> - Professional feedback</li>
            <li><strong>24/7 premium support</strong> - Priority customer service</li>
            <li><strong>Advanced search filters</strong> - Salary range, company size, etc.</li>
            <li><strong>Interview scheduling tools</strong> - Integrated calendar system</li>
            <li><strong>Skills assessment tests</strong> - Validate your expertise</li>
          </ul>
          
          <h3>Which Plan is Right for You?</h3>
          <p><strong>Free Plan</strong> is perfect if you're just starting your job search, applying casually, or testing the platform. It gives you access to core features without financial commitment.</p>
          
          <p><strong>Premium Plan</strong> is ideal for serious job seekers, career changers, or anyone wanting to maximize their chances. The advanced features can significantly reduce your job search time and increase offer rates.</p>
          
          <div class="banner-tip">
            <strong>Pro Tip:</strong> Start with the free plan and upgrade to premium when you're ready to accelerate your search. Many users see a 3x increase in interview requests after upgrading.
          </div>
        `,
    },
    "optimize-profile": {
      id: "optimize-profile",
      title: "How to Optimize Your Profile for Better Job Matches",
      description:
        "Learn proven strategies to enhance your profile and increase your chances of finding the perfect job match.",
      category: "profile",
      readTime: "8 min read",
      author: "Sarah Chen - Career Coach",
      date: "2024-01-12",
      content: `
          <h2>Transform Your Profile into a Job Magnet</h2>
          <p>A well-optimized profile is your most powerful tool in today's competitive job market. Follow these strategies to stand out and attract the right opportunities.</p>
          
          <h3>1. Craft a Compelling Headline</h3>
          <p>Your headline is the first thing employers see. Make it count!</p>
          <ul>
            <li><strong>Do:</strong> "Senior Software Engineer | React & Node.js Specialist | Cloud Architecture"</li>
            <li><strong>Don't:</strong> "Looking for a job" or "Unemployed"</li>
          </ul>
          
          <h3>2. Write a Powerful Summary</h3>
          <p>Your summary should tell your professional story in 3-4 sentences:</p>
          <ul>
            <li>Start with your current role and expertise</li>
            <li>Highlight key achievements with metrics</li>
            <li>Mention your career goals and passions</li>
            <li>Include relevant keywords for your industry</li>
          </ul>
          
          <h3>3. Showcase Your Experience Effectively</h3>
          <p>For each position, focus on accomplishments rather than duties:</p>
          <ul>
            <li>Use action verbs: "Led," "Developed," "Increased," "Reduced"</li>
            <li>Include quantifiable results: "Improved performance by 40%"</li>
            <li>Highlight promotions and career progression</li>
          </ul>
          
          <h3>4. Skills and Endorsements</h3>
          <ul>
            <li>List both technical and soft skills</li>
            <li>Prioritize skills most relevant to your target roles</li>
            <li>Get endorsements from colleagues and managers</li>
            <li>Take skill assessment tests to validate expertise</li>
          </ul>
          
          <h3>5. Complete All Profile Sections</h3>
          <p>A 100% complete profile gets 40% more views:</p>
          <ul>
            <li>Professional photo (headshot with neutral background)</li>
            <li>Education and certifications</li>
            <li>Projects and portfolio links</li>
            <li>Volunteer experience</li>
            <li>Languages and additional competencies</li>
          </ul>
          
          <div class="banner-tip">
            <strong>Quick Win:</strong> Profiles with professional photos receive 21x more profile views and 9x more connection requests.
          </div>
        `,
    },
    "understanding-our-job-matching-algorithm": {
      id: "understanding-our-job-matching-algorithm",
      title: "Understanding Our Job Matching Algorithm",
      description:
        "Get insights into how our algorithm matches your skills and preferences with the right job opportunities.",
      category: "technology",
      readTime: "6 min read",
      author: "Dr. Alex Rodriguez - Lead Data Scientist",
      date: "2024-01-10",
      content: `
          <h2>How We Connect You with Perfect Opportunities</h2>
          <p>Our job matching algorithm uses advanced machine learning to understand your profile, preferences, and behavior to surface the most relevant job opportunities.</p>
          
          <h3>Key Factors We Consider</h3>
          
          <h4>1. Profile Compatibility (40%)</h4>
          <ul>
            <li><strong>Skills Match:</strong> How well your skills align with job requirements</li>
            <li><strong>Experience Level:</strong> Years of experience and seniority match</li>
            <li><strong>Education:</strong> Relevant degrees and certifications</li>
            <li><strong>Industry Background:</strong> Previous industry experience</li>
          </ul>
          
          <h4>2. Preference Alignment (30%)</h4>
          <ul>
            <li><strong>Location Preferences:</strong> Remote, hybrid, or specific cities</li>
            <li><strong>Salary Expectations:</strong> Alignment with offered compensation</li>
            <li><strong>Company Culture:</strong> Size, values, and work environment</li>
            <li><strong>Job Type:</strong> Full-time, part-time, contract preferences</li>
          </ul>
          
          <h4>3. Behavioral Signals (20%)</h4>
          <ul>
            <li><strong>Application History:</strong> What types of jobs you apply to</li>
            <li><strong>Profile Views:</strong> Which companies view your profile</li>
            <li><strong>Search Behavior:</strong> What you search for actively</li>
            <li><strong>Engagement Patterns:</strong> Which job alerts you open</li>
          </ul>
          
          <h4>4. Market Dynamics (10%)</h4>
          <ul>
            <li><strong>Demand for Your Skills:</strong> Current market trends</li>
            <li><strong>Company Hiring Urgency:</strong> How quickly they need to fill roles</li>
            <li><strong>Competitive Landscape:</strong> Number of applicants for similar roles</li>
          </ul>
          
          <h3>How to Improve Your Match Score</h3>
          <ul>
            <li><strong>Complete your profile thoroughly</strong> - More data means better matches</li>
            <li><strong>Be specific about preferences</strong> - Clear preferences yield better results</li>
            <li><strong>Regularly update skills</strong> - Keep your profile current</li>
            <li><strong>Engage with the platform</strong> - Apply to relevant jobs and save interesting listings</li>
          </ul>
          
          <div class="banner-insight">
            <strong>Algorithm Insight:</strong> Users who complete all profile sections receive 60% more high-quality matches than those with incomplete profiles.
          </div>
        `,
    },
    "managing-your-application-pipeline": {
      id: "managing-your-application-pipeline",
      title: "Managing Your Application Pipeline",
      description:
        "Effective strategies to organize and track your job applications for better results.",
      category: "applications",
      readTime: "7 min read",
      author: "Michael Thompson - Recruitment Expert",
      date: "2024-01-08",
      content: `
          <h2>Master Your Job Search Pipeline</h2>
          <p>An organized application pipeline is crucial for managing multiple opportunities and making informed decisions throughout your job search journey.</p>
          
          <h3>The 5-Stage Application Pipeline</h3>
          
          <h4>Stage 1: Research & Preparation</h4>
          <ul>
            <li><strong>Target Companies:</strong> Research potential employers</li>
            <li><strong>Job Requirements:</strong> Understand role expectations</li>
            <li><strong>Custom Materials:</strong> Tailor resume and cover letter</li>
            <li><strong>Network Research:</strong> Identify connections at target companies</li>
          </ul>
          
          <h4>Stage 2: Application Submitted</h4>
          <ul>
            <li><strong>Track Submission Date</strong></li>
            <li><strong>Save Job Description</strong></li>
            <li><strong>Note Application Method</strong> (direct, referral, etc.)</li>
            <li><strong>Set Follow-up Reminder</strong> (7-10 days)</li>
          </ul>
          
          <h4>Stage 3: Initial Contact</h4>
          <ul>
            <li><strong>Recruiter Screen:</strong> Prepare for initial calls</li>
            <li><strong>Take Notes:</strong> Document conversation highlights</li>
            <li><strong>Research Interviewers:</strong> Before technical rounds</li>
            <li><strong>Send Thank You Notes:</strong> Within 24 hours</li>
          </ul>
          
          <h4>Stage 4: Interview Process</h4>
          <ul>
            <li><strong>Track Interview Dates</strong> and types</li>
            <li><strong>Document Questions Asked</strong></li>
            <li><strong>Record Your Answers</strong> for improvement</li>
            <li><strong>Gather Feedback</strong> after each round</li>
          </ul>
          
          <h4>Stage 5: Offer & Decision</h4>
          <ul>
            <li><strong>Compare Offers:</strong> Compensation, benefits, culture</li>
            <li><strong>Negotiation Strategy:</strong> Know your worth</li>
            <li><strong>Reference Checks:</strong> Prepare your references</li>
            <li><strong>Resignation Planning:</strong> Professional transition</li>
          </ul>
          
          <h3>Tools for Pipeline Management</h3>
          <ul>
            <li><strong>Our Built-in Tracker:</strong> Use our application dashboard</li>
            <li><strong>Spreadsheet Template:</strong> Company, role, date, status, contacts</li>
            <li><strong>Calendar System:</strong> Schedule follow-ups and interviews</li>
            <li><strong>Document Repository:</strong> Store tailored resumes and cover letters</li>
          </ul>
          
          <div class="banner-tip">
            <strong>Pro Tip:</strong> Aim to maintain 10-15 active applications in various stages. This provides enough opportunities without spreading yourself too thin.
          </div>
        `,
    },
    "how-to-set-up-effective-job-alerts": {
      id: "how-to-set-up-effective-job-alerts",
      title: "How to Set Up Effective Job Alerts",
      description:
        "Create smart job alerts that deliver relevant opportunities directly to your inbox.",
      category: "tools",
      readTime: "4 min read",
      author: "Product Team",
      date: "2024-01-05",
      content: `
          <h2>Never Miss Your Dream Job Opportunity</h2>
          <p>Well-configured job alerts can save you hours of manual searching and ensure you're among the first applicants for new opportunities.</p>
          
          <h3>Creating High-Quality Job Alerts</h3>
          
          <h4>1. Be Specific with Keywords</h4>
          <ul>
            <li><strong>Use exact job titles:</strong> "Senior Product Manager" not just "Manager"</li>
            <li><strong>Include synonyms:</strong> "Software Engineer OR Developer"</li>
            <li><strong>Add required technologies:</strong> "React AND TypeScript"</li>
            <li><strong>Exclude unwanted terms:</strong> "Senior -Junior -Entry"</li>
          </ul>
          
          <h4>2. Set Geographic Parameters</h4>
          <ul>
            <li><strong>Specific Cities:</strong> For on-site roles</li>
            <li><strong>Remote Options:</strong> "Remote OR Anywhere"</li>
            <li><strong>Hybrid Flexibility:</strong> Include commutable distances</li>
            <li><strong>Multiple Locations:</strong> For relocation opportunities</li>
          </ul>
          
          <h4>3. Refine by Company & Industry</h4>
          <ul>
            <li><strong>Company Size:</strong> Startup, mid-size, or enterprise</li>
            <li><strong>Industry Focus:</strong> Tech, healthcare, finance, etc.</li>
            <li><strong>Specific Companies:</strong> Your dream employers</li>
            <li><strong>Exclude Industries:</strong> Remove irrelevant sectors</li>
          </ul>
          
          <h4>4. Experience Level & Salary</h4>
          <ul>
            <li><strong>Match your level:</strong> Mid-level, Senior, Director</li>
            <li><strong>Salary requirements:</strong> Set minimum acceptable range</li>
            <li><strong>Employment type:</strong> Full-time, contract, part-time</li>
          </ul>
          
          <h3>Advanced Alert Strategies</h3>
          
          <h4>Create Multiple Focused Alerts</h4>
          <p>Instead of one broad alert, create several specific ones:</p>
          <ul>
            <li><strong>Primary Role Alert:</strong> Your ideal position</li>
            <li><strong>Secondary Role Alert:</strong> Related positions</li>
            <li><strong>Dream Company Alert:</strong> Specific target employers</li>
            <li><strong>Location-Specific Alert:</strong> By geographic preference</li>
          </ul>
          
          <h4>Optimize Alert Frequency</h4>
          <ul>
            <li><strong>Daily:</strong> For urgent, active searches</li>
            <li><strong>Weekly Digest:</strong> For casual browsing</li>
            <li><strong>Real-time:</strong> For highly competitive roles</li>
          </ul>
          
          <div class="banner-data">
            <strong>Data Point:</strong> Candidates who apply within 3 days of job posting are 3x more likely to get an interview. Effective alerts give you this early advantage.
          </div>
        `,
    },
    "resume-tips-that-get-you-noticed": {
      id: "resume-tips-that-get-you-noticed",
      title: "Resume Tips That Get You Noticed",
      description:
        "Professional resume writing tips that will make your application stand out to employers.",
      category: "resume",
      readTime: "10 min read",
      author: "HR Professionals",
      date: "2024-01-03",
      content: `
          <h2>Craft a Resume That Commands Attention</h2>
          <p>In today's competitive market, your resume needs to make an impact in just 6-7 seconds. Follow these proven strategies to create a resume that gets results.</p>
          
          <h3>Essential Resume Components</h3>
          
          <h4>1. Professional Summary That Sells</h4>
          <ul>
            <li>Keep it to 3-4 powerful sentences</li>
            <li>Include your years of experience</li>
            <li>Mention key achievements and specialties</li>
            <li>Tailor it to the specific role you're targeting</li>
          </ul>
          
          <h4>2. Achievement-Oriented Experience Section</h4>
          <p>For each position, focus on accomplishments using the STAR method:</p>
          <ul>
            <li><strong>Situation:</strong> Context of your work</li>
            <li><strong>Task:</strong> What you were responsible for</li>
            <li><strong>Action:</strong> What you actually did</li>
            <li><strong>Result:</strong> Quantifiable outcomes</li>
          </ul>
          
          <h4>3. Skills Section That Matches Job Descriptions</h4>
          <ul>
            <li><strong>Technical Skills:</strong> Programming languages, tools, software</li>
            <li><strong>Soft Skills:</strong> Leadership, communication, problem-solving</li>
            <li><strong>Industry-Specific Skills:</strong> Certifications, methodologies</li>
            <li><strong>Match keywords</strong> from job descriptions you're targeting</li>
          </ul>
          
          <h3>Formatting for Maximum Impact</h3>
          
          <h4>Layout Principles</h4>
          <ul>
            <li><strong>Clean, professional design</strong> - No distracting graphics</li>
            <li><strong>Consistent formatting</strong> throughout the document</li>
            <li><strong>Ample white space</strong> for easy reading</li>
            <li><strong>Clear section headings</strong> for quick scanning</li>
          </ul>
          
          <h4>Length Guidelines</h4>
          <ul>
            <li><strong>Entry-level:</strong> 1 page maximum</li>
            <li><strong>Mid-career:</strong> 1-2 pages</li>
            <li><strong>Senior/Executive:</strong> 2-3 pages as needed</li>
            <li><strong>Academic/Research:</strong> CV format, no page limit</li>
          </ul>
          
          <h3>ATS Optimization Strategies</h3>
          <p>Most companies use Applicant Tracking Systems. Ensure your resume gets through:</p>
          <ul>
            <li><strong>Use standard section headings:</strong> "Work Experience," "Education"</li>
            <li><strong>Include keywords</strong> from the job description</li>
            <li><strong>Avoid tables, columns, and graphics</strong></li>
            <li><strong>Use common fonts:</strong> Arial, Calibri, Times New Roman</li>
            <li><strong>Save as .docx or PDF</strong> (unless specified otherwise)</li>
          </ul>
          
          <h3>Quantifiable Achievement Examples</h3>
          <p>Transform generic responsibilities into impressive achievements:</p>
          
          <table class="w-full border-collapse border border-gray-300">
            <thead>
              <tr class="bg-gray-100">
                <th class="border border-gray-300 p-2 text-left">Instead of...</th>
                <th class="border border-gray-300 p-2 text-left">Use...</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td class="border border-gray-300 p-2">"Responsible for sales"</td>
                <td class="border border-gray-300 p-2">"Increased sales by 45% in Q2 2023"</td>
              </tr>
              <tr>
                <td class="border border-gray-300 p-2">"Managed a team"</td>
                <td class="border border-gray-300 p-2">"Led a 12-person team that delivered projects 20% under budget"</td>
              </tr>
              <tr>
                <td class="border border-gray-300 p-2">"Improved processes"</td>
                <td class="border border-gray-300 p-2">"Reduced processing time by 30% through automation"</td>
              </tr>
            </tbody>
          </table>
          
          <div class="banner-tip">
            <strong>Final Tip:</strong> Have 2-3 trusted colleagues review your resume. Fresh eyes catch errors and can provide valuable feedback on clarity and impact.
          </div>
        `,
    },
  };

  const article = articleId ? articles[articleId] : null;

  // Reading progress calculation
  useEffect(() => {
    const calculateReadingProgress = () => {
      if (contentRef.current) {
        const element = contentRef.current;
        const totalHeight = element.clientHeight - window.innerHeight;
        const windowScrollTop = window.scrollY - element.offsetTop + 100;

        if (windowScrollTop <= 0) {
          setReadingProgress(0);
        } else if (windowScrollTop > totalHeight) {
          setReadingProgress(100);
        } else {
          setReadingProgress((windowScrollTop / totalHeight) * 100);
        }
      }
    };

    window.addEventListener("scroll", calculateReadingProgress);
    window.addEventListener("resize", calculateReadingProgress);

    return () => {
      window.removeEventListener("scroll", calculateReadingProgress);
      window.removeEventListener("resize", calculateReadingProgress);
    };
  }, [article]);

  // Process content for dark mode
  const processContentForTheme = (content: string) => {
    let processedContent = content;

    // Replace banner classes with theme-specific styles
    const bannerReplacements = {
      "banner-tip": isDark
        ? "bg-amber-900/20 border border-amber-700 rounded-lg p-4 my-6 text-amber-200"
        : "bg-amber-50 border border-amber-200 rounded-lg p-4 my-6 text-amber-800",
      "banner-insight": isDark
        ? "bg-sky-900/20 border border-sky-700 rounded-lg p-4 my-6 text-sky-200"
        : "bg-sky-50 border border-sky-200 rounded-lg p-4 my-6 text-sky-800",
      "banner-data": isDark
        ? "bg-emerald-900/20 border border-emerald-700 rounded-lg p-4 my-6 text-emerald-200"
        : "bg-emerald-50 border border-emerald-200 rounded-lg p-4 my-6 text-emerald-800",
      "banner-info": isDark
        ? "bg-blue-900/20 border border-blue-700 rounded-lg p-4 my-6 text-blue-200"
        : "bg-blue-50 border border-blue-200 rounded-lg p-4 my-6 text-blue-800",
    };

    Object.entries(bannerReplacements).forEach(([key, value]) => {
      processedContent = processedContent.replace(
        new RegExp(`class="${key}"`, "g"),
        `class="${value}"`,
      );
    });

    return processedContent;
  };

  if (!article) {
    return (
      <div
        className={`min-h-screen ${isDark ? "bg-gray-900" : "bg-gray-50"} flex items-center justify-center`}
      >
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Article Not Found</h2>
          <button
            onClick={() => navigate("/articles")}
            className={`text-sky-600 hover:text-sky-700 transition-colors`}
          >
            Back to Articles
          </button>
        </div>
      </div>
    );
  }

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: article.title,
          text: article.description,
          url: window.location.href,
        });
      } catch (err) {
        console.log("Error sharing:", err);
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert("Link copied to clipboard!");
    }
  };

  const toggleBookmark = () => {
    setIsBookmarked(!isBookmarked);
    // Here you would typically save to localStorage or your backend
  };

  return (
    <div
      className={`min-h-screen ${isDark ? "bg-gray-900 text-white" : "bg-gray-50 text-gray-900"} transition-colors duration-200`}
    >
      {/* Reading Progress Bar */}
      <div
        className={`fixed top-0 left-0 h-1 z-50 transition-all duration-150 bg-gradient-to-r from-sky-400 to-blue-500`}
        style={{ width: `${readingProgress}%` }}
      />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <button
          onClick={() => navigate("/articles")}
          className={`flex items-center mb-6 text-sm font-medium transition-colors text-sky-600 hover:text-sky-700 dark:text-sky-400 dark:hover:text-sky-300`}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Articles
        </button>

        {/* Article Header */}
        <header className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <span
              className={`text-sm font-medium px-3 py-1 rounded-full ${
                isDark
                  ? "bg-sky-900/30 text-sky-300"
                  : "bg-sky-100 text-sky-700"
              }`}
            >
              {article.category.charAt(0).toUpperCase() +
                article.category.slice(1)}
            </span>
            <div className="flex items-center gap-3">
              <button
                onClick={toggleBookmark}
                className={`flex items-center text-sm transition-colors ${
                  isBookmarked
                    ? "text-yellow-500 dark:text-yellow-400"
                    : "text-gray-500 hover:text-sky-600 dark:text-gray-400 dark:hover:text-sky-400"
                }`}
              >
                <Bookmark
                  className="h-4 w-4 mr-1"
                  fill={isBookmarked ? "currentColor" : "none"}
                />
                Bookmark
              </button>
              <button
                onClick={handleShare}
                className={`flex items-center text-sm transition-colors text-gray-500 hover:text-sky-600 dark:text-gray-400 dark:hover:text-sky-400`}
              >
                <Share2 className="h-4 w-4 mr-1" />
                Share
              </button>
            </div>
          </div>

          <h1 className="text-4xl font-bold mb-4 leading-tight bg-gradient-to-r from-sky-600 to-blue-600 bg-clip-text text-transparent">
            {article.title}
          </h1>
          <p
            className={`text-xl mb-6 leading-relaxed ${
              isDark ? "text-gray-300" : "text-gray-600"
            }`}
          >
            {article.description}
          </p>

          <div
            className={`flex flex-wrap items-center gap-4 text-sm ${
              isDark ? "text-gray-400" : "text-gray-500"
            }`}
          >
            <div className="flex items-center">
              <User className="h-4 w-4 mr-2" />
              {article.author}
            </div>
            <div className="flex items-center">
              <Calendar className="h-4 w-4 mr-2" />
              {new Date(article.date).toLocaleDateString()}
            </div>
            <div className="flex items-center">
              <Clock className="h-4 w-4 mr-2" />
              {article.readTime}
            </div>
            <div className="flex items-center">
              <Eye className="h-4 w-4 mr-2" />
              {Math.floor(Math.random() * 1000) + 500} views
            </div>
          </div>
        </header>

        {/* Article Content */}
        <article
          ref={contentRef}
          className={`max-w-none mx-auto ${
            isDark
              ? "prose prose-invert prose-headings:text-white prose-p:text-gray-300 prose-strong:text-white prose-li:text-gray-300 prose-table:border-gray-700 prose-td:border-gray-700 prose-th:border-gray-700 prose-th:bg-gray-800 prose-blockquote:text-gray-400 prose-code:text-gray-300 prose-code:bg-gray-800 prose-pre:bg-gray-800"
              : "prose prose-gray prose-headings:text-gray-900 prose-p:text-gray-600 prose-strong:text-gray-900 prose-li:text-gray-600 prose-table:border-gray-300 prose-td:border-gray-300 prose-th:border-gray-300 prose-th:bg-gray-100 prose-blockquote:text-gray-500 prose-code:text-gray-700 prose-code:bg-gray-100 prose-pre:bg-gray-100"
          } 
          prose-headings:font-bold prose-h2:text-2xl prose-h3:text-xl prose-h4:text-lg 
          prose-table:border prose-td:border prose-th:border prose-td:p-3 prose-th:p-3 
          prose-a:text-sky-600 prose-a:no-underline hover:prose-a:underline prose-a:dark:text-sky-400
          prose-blockquote:border-l-sky-400 prose-blockquote:italic
          prose-img:rounded-lg prose-img:shadow-lg
          leading-relaxed`}
          dangerouslySetInnerHTML={{
            __html: processContentForTheme(article.content),
          }}
        />

        {/* Article Footer */}
        <footer className="max-w-none mx-auto mt-12 pt-8 border-t border-gray-700">
          <div
            className={`text-sm ${isDark ? "text-gray-400" : "text-gray-500"}`}
          >
            <p>
              Found this article helpful? Share it with others who might
              benefit.
            </p>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default ArticleDetail;
